import { useInViewport } from '@mantine/hooks';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useMemo } from 'react';
import { PiMagnifyingGlass } from 'react-icons/pi';
import { FormattedMessage, useIntl } from 'react-intl';
import { LoadMoreTrigger } from '@_/components';
import { buttonCommonI18n } from '@_/i18n-common';
import { MAX_TEXT_POST_WIDTH } from '@_/theme/constants.ts';
import { Button, Heading, Skeleton, SkeletonHeading } from '@_/ui';
import {
  getInfiniteReactQueryOptions,
  getItemsInInfiniteQuery,
  getReactQueryOptions
} from '@_/utils-frontend';
import { publicApiQueryClient } from '#src/api.ts';
import { AppShell, useAppShell } from '#src/components/AppShell/index.ts';
import { EnhancedPostFeed } from '#src/components/EnhancedPostFeed/index.ts';
import { GlobalSearch } from '#src/components/GlobalSearch/index.ts';
import { VenueList, VenueListSkeleton } from '#src/components/VenueList/index.ts';
import { placeholderI18n } from '#src/i18nMessages/index.ts';
import { MAX_PAGE_WIDTH } from '#src/theme/constants.ts';

export function HomePage() {
  const { mobileSearch } = useAppShell();
  const intl = useIntl();
  const { ref: heroRef, inViewport: isHeroInView } = useInViewport();

  const homepageQuery = useQuery(getReactQueryOptions(publicApiQueryClient.homepageMetadata.list));

  const featuredVenues = useMemo(
    () => homepageQuery.data?.data.featuredVenues || [],
    [homepageQuery.data]
  );

  const feedQuery = useInfiniteQuery(
    getInfiniteReactQueryOptions(publicApiQueryClient.feed.list, {})
  );

  const { items: posts } = useMemo(() => getItemsInInfiniteQuery(feedQuery.data), [feedQuery.data]);

  return (
    <AppShell showHeaderSearch={!isHeroInView} stickyHeader={!isHeroInView}>
      <div
        ref={heroRef}
        className={clsx(MAX_PAGE_WIDTH, 'relative mx-auto px-4 py-10 md:py-16 lg:py-20')}
      >
        <section className="z-1">
          <h1 className="mb-8 hidden text-center text-3xl leading-tight font-black sm:block lg:text-4xl">
            <FormattedMessage id="home.hero-tagline" defaultMessage="Learning starts here" />
          </h1>

          <div className="flex flex-col gap-y-3 sm:hidden">
            <div className="flex items-center gap-x-3 rounded-full border border-gray-200 px-4">
              <PiMagnifyingGlass aria-hidden="true" className="size-5 text-gray-900" />
              <input
                className="grow resize-none appearance-none border-0 bg-transparent px-0 py-3 text-sm text-gray-900 placeholder-gray-400 focus:ring-0 focus:outline-hidden"
                onFocus={() => mobileSearch.open()}
                placeholder={intl.formatMessage(placeholderI18n.globalSearch)}
                type="text"
              />
            </div>
            <div>
              <Button
                color="primary"
                fillContainer
                onClick={() => mobileSearch.open()}
                rounded
                size="lg"
              >
                <FormattedMessage {...buttonCommonI18n.search} />
              </Button>
            </div>
          </div>

          <div className="mx-auto hidden w-full max-w-xl sm:block lg:max-w-2xl">
            <GlobalSearch size="lg" />
          </div>
        </section>
      </div>

      {(homepageQuery.isPending || !!featuredVenues.length) && (
        <section className="bg-zinc-50 pt-8 pb-10 md:pt-12 md:pb-12">
          <div className={clsx(MAX_PAGE_WIDTH, 'mx-auto px-4 md:px-8')}>
            {homepageQuery.isPending ? (
              <Skeleton className="max-w-60">
                <SkeletonHeading level={2} />
              </Skeleton>
            ) : (
              <Heading level={2}>
                <FormattedMessage
                  id="heading.recommended-for-you"
                  defaultMessage="Recommended for you"
                />
              </Heading>
            )}
            <div className="mt-6">
              {homepageQuery.isPending ? (
                <VenueListSkeleton numColumns={4} />
              ) : (
                <VenueList items={featuredVenues} numColumns={4} />
              )}
            </div>
          </div>
        </section>
      )}

      <section
        className={clsx(
          'py-12 md:py-16',
          'px-4 [--gutter-x:--spacing(4)] md:px-8 md:[--gutter-x:--spacing(8)]'
        )}
      >
        <header className="sm:text-center">
          <Heading level={2}>
            <FormattedMessage id="heading.whats-happening" defaultMessage="What's happening" />
          </Heading>
        </header>

        <div className={clsx('mx-auto mt-8 sm:mt-12', MAX_TEXT_POST_WIDTH)}>
          <EnhancedPostFeed bleed="mobile" items={posts} />

          <LoadMoreTrigger
            hasMore={feedQuery.hasNextPage}
            loading={feedQuery.isFetchingNextPage}
            onFetchMore={feedQuery.fetchNextPage}
            scrollMargin={100}
            trigger="scroll"
          />
        </div>
      </section>
    </AppShell>
  );
}
